import React, { useRef, useMemo, useEffect, useCallback } from 'react'
import { Alert } from '@material-tailwind/react'
import { useTranslation } from 'react-i18next'
import Mapbox, { NavigationControl, ScaleControl } from 'react-map-gl'
import XpSpotDraggableMarker from '../Map/XpSpotDraggableMarker'
import { DEFAULT_MAP_ZOOM, MAPBOX_TOKEN } from '../utils/constants/mapbox.constants'
import { useUserPreferences } from '../contexts/UserPreferencesContext'
import MapStyleSelector from './MapStyleSelector'
import { useFeatureAuth } from '../hooks/authorization'
import { FEATURES } from '../utils/constants/features.constants'
import TerrainSourceLayer from '../Map/TerrainSourceLayer'
import Buildings3DLayer from '../Map/Buildings3DLayer'
import { useDarkMode } from '../contexts/DarkModeContext'

interface SpotPositionPreviewProps {
  latitude?: number | string
  setLatitude: (lat: number) => void
  longitude?: number | string
  setLongitude: (lng: number) => void
  radius?: number | string
  deadband?: number | string
  interactive?: boolean
}

const SpotPositionPreview: React.FC<SpotPositionPreviewProps> = ({
  latitude,
  setLatitude,
  longitude,
  setLongitude,
  radius,
  deadband,
  interactive,
}) => {
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()
  const mapRef = useRef<React.ComponentRef<typeof Mapbox>>(null)

  const mapboxInitialView = useMemo(
    () =>
      latitude && longitude
        ? { longitude: Number(longitude), latitude: Number(latitude), zoom: DEFAULT_MAP_ZOOM }
        : null,
    [latitude, longitude]
  )

  useEffect(() => {
    if (!latitude || !longitude) return
    mapRef?.current?.easeTo({
      center: [Number(longitude), Number(latitude)],
      duration: 500,
    })
  }, [latitude, longitude])

  const updateLatLng = useCallback(
    (evt: { lngLat: { lng: number; lat: number } }) => {
      if (!evt?.lngLat) return
      const { lat, lng } = evt.lngLat
      setLatitude(lat)
      setLongitude(lng)
    },
    [setLatitude, setLongitude]
  )
  const { mapStyleUrl, apply3DMap } = useUserPreferences()
  const advMapsAuth = useFeatureAuth(FEATURES.ADVANCED_MAP_STYLES)

  return (
    mapboxInitialView && (
      <div className="w-full flex flex-col justify-center">
        {interactive && (
          <div className="inline-flex mx-auto justify-center">
            <Alert variant={darkMode ? 'filled' : 'ghost'} color="blue-gray" className="my-2 py-1">
              <small className="mx-0">{t('spotPosition.interactiveInstructions')}</small>
            </Alert>
          </div>
        )}
        <div className="w-full h-[650px]" data-testid="agami-geo-pos-preview-map">
          <Mapbox
            ref={mapRef}
            mapboxAccessToken={MAPBOX_TOKEN}
            mapStyle={mapStyleUrl}
            initialViewState={mapboxInitialView}
            attributionControl={false}
            onDblClick={updateLatLng}
            doubleClickZoom={false}
            style={{
              height: '100%',
              width: '100%',
            }}
            terrain={{
              source: 'mapbox-dem',
              exaggeration: apply3DMap ? 1 : 0,
            }}
            maxPitch={75}
          >
            <NavigationControl />
            <ScaleControl position="bottom-right" />
            {latitude && longitude && (
              <XpSpotDraggableMarker
                position={{
                  id: 'agami-preview-position',
                  latitude: Number(latitude),
                  longitude: Number(longitude),
                  radius: Number(radius),
                  deadband: Number(deadband),
                }}
                onDragStart={() => {}}
                onDragEnd={updateLatLng}
              />
            )}
            {advMapsAuth && (
              <>
                <div className="absolute p-3 bottom-6 left-1">
                  <MapStyleSelector />
                </div>
                <TerrainSourceLayer />
                <Buildings3DLayer />
              </>
            )}
          </Mapbox>
        </div>
      </div>
    )
  )
}

export default SpotPositionPreview
